/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */
import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React examples
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import PieChart from "examples/Charts/PieChart";
import DataTable from "examples/Tables/DataTable";

// Utilidades de exportación
import { exportDashboardToPDF, exportToExcel, handlePrint as printPage } from "utils/exportHelpers";

export default function ReportesOperativos() {
  const [area, setArea] = useState("ventas");
  const [periodo, setPeriodo] = useState("mes");
  const [sucursal, setSucursal] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para datos de Ventas
  const [kpisVentas, setKpisVentas] = useState([]);
  const [ventasPorProducto, setVentasPorProducto] = useState({ labels: [], datasets: [] });
  const [ventasPorCanal, setVentasPorCanal] = useState({ labels: [], datasets: [] });
  const [tendenciaVentas, setTendenciaVentas] = useState({ labels: [], datasets: [] });
  const [topVendedores, setTopVendedores] = useState({ columns: [], rows: [] });

  // Estados para datos de Inventario
  const [kpisInventario, setKpisInventario] = useState([]);
  const [stockPorCategoria, setStockPorCategoria] = useState({ labels: [], datasets: [] });
  const [movimientosInventario, setMovimientosInventario] = useState({ labels: [], datasets: [] });
  const [bajaRotacion, setProductosBajaRotacion] = useState({ columns: [], rows: [] });
  const [productosBajo, setProductosBajo] = useState({ columns: [], rows: [] });

  // Cargar datos según el área seleccionada
  useEffect(() => {
    const fetchDatos = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          periodo: periodo,
        };

        if (empresa) params.empresa_id = empresa;
        if (sucursal) params.sucursal_id = sucursal;

        if (area === "ventas") {
          // Cargar datos de ventas
          const response = await axios.get(API_ENDPOINTS.REPORTES.VENTAS_OPERATIVO, { params });
          const data = response.data;

          const formatCurrency = (value) => {
            const formatted = new Intl.NumberFormat("es-MX", {
              notation: "compact",
              compactDisplay: "short",
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }).format(value);
            return `$${formatted}`;
          };

          // KPIs de Ventas
          setKpisVentas([
            {
              color: "success",
              icon: "shopping_cart",
              title: "Ventas Totales",
              count: formatCurrency(data.kpis.total_ventas),
              percentage: { color: "success", amount: "+18%", label: "vs mes anterior" },
            },
            {
              color: "info",
              icon: "receipt_long",
              title: "Número de Transacciones",
              count: data.kpis.numero_ventas.toString(),
              percentage: {
                color: "info",
                amount: formatCurrency(data.kpis.ticket_promedio),
                label: "promedio",
              },
            },
            {
              color: "warning",
              icon: "people",
              title: "Clientes Atendidos",
              count: data.kpis.numero_ventas.toString(),
              percentage: { color: "info", amount: "-", label: "en período" },
            },
            {
              color: "primary",
              icon: "trending_up",
              title: "Ticket Promedio",
              count: formatCurrency(data.kpis.ticket_promedio),
              percentage: { color: "success", amount: "+5%", label: "vs mes anterior" },
            },
          ]);

          // Tendencia de Ventas
          if (data.tendencia_ventas && data.tendencia_ventas.length > 0) {
            setTendenciaVentas({
              labels: data.tendencia_ventas.map((item) => item.mes),
              datasets: [
                {
                  label: "Ventas",
                  color: "success",
                  data: data.tendencia_ventas.map((item) => parseFloat(item.ingresos)),
                },
              ],
            });
          }

          // Datos estáticos temporales para ventas
          setVentasPorProducto({
            labels: ["Sin datos"],
            datasets: [{
              label: "Productos",
              backgroundColors: ["info"],
              data: [0],
            }],
          });

          setVentasPorCanal({
            labels: ["Directa"],
            datasets: [{
              label: "Canal",
              backgroundColors: ["success"],
              data: [100],
            }],
          });

          setTopVendedores({
            columns: [
              { Header: "vendedor", accessor: "vendedor", width: "30%" },
              { Header: "ventas", accessor: "ventas", width: "20%" },
              { Header: "monto", accessor: "monto", width: "25%" },
              { Header: "comisión", accessor: "comision", width: "25%" },
            ],
            rows: [{ vendedor: "En desarrollo", ventas: "0", monto: "$0", comision: "$0" }],
          });
        } else if (area === "inventario") {
          // Cargar datos de inventario
          const response = await axios.get(API_ENDPOINTS.REPORTES.INVENTARIO_OPERATIVO, { params });
          const data = response.data;

          const formatCurrency = (value) => {
            const formatted = new Intl.NumberFormat("es-MX", {
              notation: "compact",
              compactDisplay: "short",
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }).format(value);
            return `$${formatted}`;
          };

          // KPIs de Inventario
          setKpisInventario([
            {
              color: "info",
              icon: "inventory_2",
              title: "Valor Total Inventario",
              count: formatCurrency(data.kpis.valor_total),
              percentage: { color: "info", amount: "-", label: "inventario total" },
            },
            {
              color: "success",
              icon: "category",
              title: "Productos Activos",
              count: data.kpis.total_productos.toString(),
              percentage: { color: "success", amount: "-", label: "productos activos" },
            },
            {
              color: "warning",
              icon: "sync",
              title: "Rotación Promedio",
              count: "45 días",
              percentage: { color: "warning", amount: "-", label: "estimado" },
            },
            {
              color: "error",
              icon: "warning",
              title: "Stock Bajo",
              count: data.kpis.productos_stock_bajo.toString(),
              percentage: {
                color: "error",
                amount: "¡Atención!",
                label: "requieren reabastecimiento",
              },
            },
          ]);

          // Productos
          if (data.productos && data.productos.length > 0) {
            setStockPorCategoria({
              labels: data.productos.map((p) => p.nombre.substring(0, 15)),
              datasets: [{
                label: "Stock",
                backgroundColors: ["info", "success", "warning", "primary", "secondary"],
                data: data.productos.map((p) => parseFloat(p.cantidad)),
              }],
            });

            setProductosBajo({
              columns: [
                { Header: "producto", accessor: "producto", width: "40%" },
                { Header: "stock", accessor: "stock", width: "20%" },
                { Header: "precio", accessor: "precio", width: "20%" },
                { Header: "valor", accessor: "valor", width: "20%" },
              ],
              rows: data.productos.slice(0, 5).map((p) => ({
                producto: p.nombre,
                stock: p.cantidad,
                precio: formatCurrency(p.precio),
                valor: formatCurrency(p.cantidad * p.precio),
              })),
            });
          }

          // Datos temporales
          setMovimientosInventario({
            labels: ["Sin datos"],
            datasets: [
              {
                label: "Movimientos",
                color: "info",
                data: [0],
              },
            ],
          });

          setProductosBajaRotacion({
            columns: [
              { Header: "producto", accessor: "producto", width: "30%" },
              { Header: "stock", accessor: "stock", width: "15%" },
              { Header: "días", accessor: "dias", width: "20%" },
              { Header: "valor", accessor: "valor", width: "20%" },
              { Header: "acción", accessor: "accion", width: "15%" },
            ],
            rows: [{ producto: "En desarrollo", stock: "0", dias: "0", valor: "$0", accion: "-" }],
          });
        } else if (area === "logistica") {
          // Datos de logística (temporales - sin endpoint aún)
          setKpisVentas([
            {
              color: "primary",
              icon: "local_shipping",
              title: "Entregas Realizadas",
              count: "0",
              percentage: { color: "info", amount: "-", label: "sin datos" },
            },
          ]);
        }
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError(err.message || "Error al cargar los datos");
      } finally {
        setLoading(false);
      }
    };

    fetchDatos();
  }, [area, periodo, empresa, sucursal]);

  // Ventas por Producto Top 10

  // Ventas por Canal

  // Tendencia de Ventas

  // Stock por Categoría

  // Movimientos de Inventario

  // Productos con Baja Rotación

  // Tabla de Top Vendedores

  // Tabla de Productos con Stock Bajo

  // Tabla de Rutas Activas

  const handleExportPDF = async () => {
    await exportDashboardToPDF(
      "reportes-operativos-content",
      `reporte-operativo-${area}-${new Date().getTime()}.pdf`
    );
  };

  const handleExportExcel = () => {
    let data = [];
    if (area === "ventas") {
      data = kpisVentas.map((kpi) => ({
        Métrica: kpi.title,
        Valor: kpi.count,
        Cambio: kpi.percentage.amount,
      }));
    } else if (area === "inventario") {
      data = kpisInventario.map((kpi) => ({
        Métrica: kpi.title,
        Valor: kpi.count,
        Estado: kpi.percentage.label,
      }));
    } else if (area === "logistica") {
      data = kpisLogistica.map((kpi) => ({
        Métrica: kpi.title,
        Valor: kpi.count,
        Estado: kpi.percentage.label,
      }));
    }

    exportToExcel(
      data,
      `Reporte ${area}`,
      `reporte-operativo-${area}-${new Date().getTime()}.xlsx`
    );
  };

  const handlePrintReport = () => {
    printPage();
  };

  const renderVentas = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6} lg={3}>
        {kpisVentas.map((kpi, index) => (
          <MDBox key={index} mb={3}>
            <ComplexStatisticsCard
              color={kpi.color}
              icon={kpi.icon}
              title={kpi.title}
              count={kpi.count}
              percentage={kpi.percentage}
            />
          </MDBox>
        ))}
      </Grid>

      <Grid item xs={12} lg={9}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Tendencia de Ventas - Comparativo Anual
                </MDTypography>
                <DefaultLineChart chart={tendenciaVentas} height="250px" />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Top 10 Productos Más Vendidos
                </MDTypography>
                <VerticalBarChart chart={ventasPorProducto} height="300px" />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Ventas por Canal
                </MDTypography>
                <PieChart chart={ventasPorCanal} height="300px" />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Top Vendedores del Período
                </MDTypography>
                <DataTable table={topVendedores} canSearch={false} />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderInventario = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6} lg={3}>
        {kpisInventario.map((kpi, index) => (
          <MDBox key={index} mb={3}>
            <ComplexStatisticsCard
              color={kpi.color}
              icon={kpi.icon}
              title={kpi.title}
              count={kpi.count}
              percentage={kpi.percentage}
            />
          </MDBox>
        ))}
      </Grid>

      <Grid item xs={12} lg={9}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Valor de Stock por Categoría
                </MDTypography>
                <PieChart chart={stockPorCategoria} height="300px" />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Movimientos de Inventario (Semanal)
                </MDTypography>
                <VerticalBarChart chart={movimientosInventario} height="300px" />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Productos con Baja Rotación
                </MDTypography>
                <HorizontalBarChart chart={bajaRotacion} height="300px" />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Productos con Stock Bajo
                </MDTypography>
                <DataTable table={productosBajo} canSearch={false} />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderLogistica = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6} lg={3}>
        {kpisLogistica.map((kpi, index) => (
          <MDBox key={index} mb={3}>
            <ComplexStatisticsCard
              color={kpi.color}
              icon={kpi.icon}
              title={kpi.title}
              count={kpi.count}
              percentage={kpi.percentage}
            />
          </MDBox>
        ))}
      </Grid>

      <Grid item xs={12} lg={9}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Evolución de Costos Logísticos
                </MDTypography>
                <DefaultLineChart chart={costosLogisticos} height="250px" />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Distribución de Entregas por Zona
                </MDTypography>
                <PieChart chart={entregasPorZona} height="300px" />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Eficiencia de Conductores (%)
                </MDTypography>
                <HorizontalBarChart chart={eficienciaConductores} height="300px" />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Rutas Activas en Tiempo Real
                </MDTypography>
                <DataTable table={rutasActivas} canSearch={false} />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderContent = () => {
    switch (area) {
      case "ventas":
        return renderVentas();
      case "inventario":
        return renderInventario();
      case "logistica":
        return renderLogistica();
      default:
        return renderVentas();
    }
  };

  return (
    <MDBox id="reportes-operativos-content">
      {/* Filtros y Controles */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={3}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Área Operativa"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                fullWidth
              >
                <MenuItem value="ventas">Ventas</MenuItem>
                <MenuItem value="inventario">Inventario</MenuItem>
                <MenuItem value="logistica">Logística</MenuItem>
                <MenuItem value="produccion">Producción</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Período"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                fullWidth
              >
                <MenuItem value="dia">Hoy</MenuItem>
                <MenuItem value="semana">Esta Semana</MenuItem>
                <MenuItem value="mes">Este Mes</MenuItem>
                <MenuItem value="trimestre">Este Trimestre</MenuItem>
                <MenuItem value="anio">Este Año</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDInput
                select
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                fullWidth
              >
                <MenuItem value="todas">Todas las Sucursales</MenuItem>
                <MenuItem value="central">Sucursal Central</MenuItem>
                <MenuItem value="norte">Sucursal Norte</MenuItem>
                <MenuItem value="sur">Sucursal Sur</MenuItem>
              </MDInput>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDBox display="flex" gap={1}>
                <MDButton variant="outlined" color="dark" size="small" onClick={handleExportPDF}>
                  <Icon>picture_as_pdf</Icon>&nbsp;PDF
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={handleExportExcel}
                >
                  <Icon>table_chart</Icon>&nbsp;Excel
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      {/* Contenido del Reporte */}
      {renderContent()}
    </MDBox>
  );
}
